import { CalendarCheck } from 'lucide-react';

import { Badge } from '@/components/badge';
import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { FULL_DATETIME_FORMAT } from '@/lib/constants';
import { formatToLocationDateTime } from '@/utils/format';
import { tv } from '@/utils/styles';

export interface SchedulesForecastCardProps {
  forecastDate: Date;
  scheduledDate?: Date;
  timezone?: string;
  hasForecast: boolean;
  isEarly: boolean;
  isDelayed: boolean;
  isArrival: boolean;
}

const SchedulesForecastCard = ({
  forecastDate,
  scheduledDate,
  timezone,
  hasForecast,
  isEarly,
  isDelayed,
  isArrival,
}: SchedulesForecastCardProps) => {
  const forecastDateString = formatToLocationDateTime(forecastDate, timezone || 'UTC', FULL_DATETIME_FORMAT);
  const originalDateString = scheduledDate
    ? formatToLocationDateTime(scheduledDate, timezone || 'UTC', FULL_DATETIME_FORMAT)
    : '';

  const { base, header, headerIcon, headerText, content, dates, dl, dt, dd } = styles();

  return (
    <div className={base()}>
      <header className={header()}>
        <div className="flex items-center gap-1.5">
          <CalendarCheck className={headerIcon()} />
          <Heading variant="h5" className={headerText()}>
            Schedule forecast
          </Heading>
        </div>
        {isEarly && <Badge variant="warning">Ahead</Badge>}
        {isDelayed && <Badge variant="negative">Behind schedule</Badge>}
        {!isDelayed && !isEarly && <Badge>On time</Badge>}
      </header>
      <article className={content()}>
        {hasForecast ? (
          <div>
            <div className={dates()}>
              <dl className={dl()}>
                <dt className={dt()}>Estimated {isArrival ? 'arrival' : 'departure'}:</dt>
                <dd className={dd()}>{forecastDateString}</dd>
              </dl>
              <dl className={dl()}>
                <dt className={dt()}>Original {isArrival ? 'arrival' : 'departure'}:</dt>
                <dd className={dd({ className: 'font-normal line-through' })}>{originalDateString}</dd>
              </dl>
            </div>
            <Text variant="sm">
              We believe this vessel is not running to schedule and are predicting it to{' '}
              {isArrival ? 'arrive' : 'depart'} on {forecastDateString}. We will continue to monitor the vessel and
              update this ETA accordingly.
            </Text>
          </div>
        ) : (
          <div>
            <div className={dates()}>
              <dl className={dl()}>
                <dt className={dt()}>Scheduled {isArrival ? 'arrival' : 'departure'}:</dt>
                <dd className={dd()}>{forecastDateString}</dd>
              </dl>
            </div>
            <Text variant="sm">
              We believe this vessel is running as per its schedule. We will continue to monitor the vessel and update
              this ETA accordingly.
            </Text>
          </div>
        )}
        <Text variant="sm" className="mb-0 italic text-grey-600">
          Fluent Cargo constantly checks for schedule adherence and predicts arrival times based on real-time port and
          vessel location data.
        </Text>
      </article>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'text-left',
    content: 'block p-4',
    header: 'flex items-center justify-between border-b border-grey-200 px-4 py-3',
    headerText: 'm-0 font-medium',
    headerIcon: 'size-4 fill-lightBlue-100',
    dates: 'mb-4 divide-y divide-grey-200',
    dl: 'm-0 grid grid-cols-2 py-1 text-baseSm',
    dt: 'text-text-primary',
    dd: 'flex items-center gap-2 rounded-md font-semibold text-grey-700',
  },
});

export { SchedulesForecastCard };
